import { useEffect, useState } from 'react';
import { useAutoRefresh } from './useAutoRefresh.js';

/**
 * §F — useMenu hook (shared by admin + vendor + mobile).
 * Calls GET /api/v1/me/menu (Vite proxy rewrites to /v1/v1/me/menu in dev).
 * Falls back to an empty tree on error — the consuming Sidebar/HomeGrid
 * shows its own EmptyState; no silent retries.
 */

export interface MenuItemActions {
  readonly view: boolean;
  readonly create: boolean;
  readonly edit: boolean;
  readonly delete: boolean;
  readonly approve: boolean;
  readonly reject: boolean;
  readonly export: boolean;
  readonly print: boolean;
}

export interface MenuItem {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly path: string;
  readonly icon: string;
  readonly section: string;
  /**
   * Heading text for `section`, supplied by the backend from the root menu
   * row's name. Absent on older backends, which fall back to the code.
   */
  readonly sectionLabel?: string;
  readonly sortOrder: number;
  readonly isVisible: boolean;
  /** 'OPERATIONAL' = exposes tenant business data. Label only; grants nothing. */
  readonly governanceScope?: string | null;
  /**
   * Per-page action grants (X-RBAC-1), unioned across the caller's roles from
   * the admin-controlled role_menu_map. Optional: older backends / mobile menu
   * responses omit it, in which case action-level gating falls back to "allow"
   * (the backend API guard remains the real enforcement boundary).
   */
  readonly actions?: MenuItemActions;
}

export interface MenuSection {
  readonly code: string;
  readonly label: string;
  readonly items: readonly MenuItem[];
}

export interface MenuTree {
  readonly items: readonly MenuItem[];
  readonly sections: readonly MenuSection[];
}

interface UseMenuState {
  readonly menu: MenuTree | null;
  readonly loading: boolean;
  readonly error: Error | null;
  /** True when the endpoint rejected the token (401/403) rather than failing. */
  readonly unauthorized: boolean;
}

const SECTION_LABELS: Readonly<Record<string, string>> = {
  OVERVIEW: 'Overview',
  ADMIN: 'Administration',
  SALES: 'Sales',
  BO: 'Back Office',
  VENDOR: 'Vendor',
  PAINTER: 'Field Force',
  QUALITY: 'Quality',
};

function groupBySection(items: readonly MenuItem[]): readonly MenuSection[] {
  const map = new Map<string, MenuItem[]>();
  for (const it of items) {
    const key = it.section || 'OVERVIEW';
    if (!map.has(key)) map.set(key, []);
    (map.get(key) as MenuItem[]).push(it);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, list]) => ({
      code,
      // The tenant's own menu name wins. A hardcoded heading that disagreed
      // with the database put "Branding Officer" above the Approval Inbox of
      // a Branding Head, who reasonably read it as someone else's menu.
      label:
        list.find((i) => !!i.sectionLabel)?.sectionLabel ??
        SECTION_LABELS[code] ??
        code,
      items: list
        .slice()
        .sort((x, y) => x.sortOrder - y.sortOrder),
    }));
}

/** Carries the 401/403 distinction through the shared cache. */
class MenuFetchError extends Error {
  readonly unauthorized: boolean;
  constructor(message: string, unauthorized: boolean) {
    super(message);
    this.unauthorized = unauthorized;
  }
}

/**
 * Every `<Can>` gate and the Sidebar all call this hook, so an uncached fetch
 * fired one `/v1/me/menu` request per mounted instance — 14+ identical calls
 * for a single page render, which trips the API rate limiter (429). The menu
 * then "fails", and the shell falls back to the offline ROUTE_REGISTRY menu.
 * One in-flight request is shared by all callers, and its result is reused for
 * a short window so a re-render burst cannot re-issue it.
 */
const MENU_CACHE_TTL_MS = 60_000;
/** How often the menu bar re-reads itself while a screen is open. */
const MENU_REFRESH_MS = 60_000;
let menuCache: { key: string; promise: Promise<MenuTree>; at: number } | null = null;

/** Test/logout hook — drops the shared menu so the next mount refetches. */
export function clearMenuCache(): void {
  menuCache = null;
}

function loadMenu(apiBase: string, token: string, channel?: 'web' | 'mobile'): Promise<MenuTree> {
  const key = `${apiBase}|${channel ?? ''}|${token}`;
  const now = Date.now();
  if (menuCache && menuCache.key === key && now - menuCache.at < MENU_CACHE_TTL_MS) {
    return menuCache.promise;
  }
  const promise = (async (): Promise<MenuTree> => {
    // `channel` scopes the response to the rows flagged for this client
    // (t018_menu_master.web_visible / .mobile_visible). Without it the web
    // shells also receive the mobile-only screens, which have no web route
    // and land on the 404 page when clicked.
    const qs = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const res = await fetch(`${apiBase}/v1/me/menu${qs}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    // A rejected token is an expired session, not an outage: reporting it
    // as a plain error would swap in the offline menu and hide the fact
    // that the user needs to sign in again.
    if (res.status === 401 || res.status === 403) {
      throw new MenuFetchError(`HTTP ${res.status}`, true);
    }
    if (!res.ok) throw new MenuFetchError(`HTTP ${res.status}`, false);
    const body = (await res.json()) as { items: MenuItem[]; sections?: string[] };
    const items = body.items ?? [];
    return { items, sections: groupBySection(items) };
  })();
  menuCache = { key, promise, at: now };
  // A failure must not be cached, or one 429 would pin the offline menu for
  // the whole TTL.
  promise.catch(() => {
    if (menuCache?.key === key) menuCache = null;
  });
  return promise;
}

export function useMenu(
  apiBase = '/api',
  token?: string | null,
  channel?: 'web' | 'mobile',
): UseMenuState {
  const [state, setState] = useState<UseMenuState>({
    menu: null,
    loading: true,
    error: null,
    unauthorized: false,
  });
  // Bumped by the timer below; the fetch effect watches it.
  const [round, setRound] = useState(0);

  // A right granted in Role Manager must reach the menu bar of everyone who
  // holds the role, on the screen they are already looking at.
  useAutoRefresh(
    () => {
      clearMenuCache();
      setRound((n) => n + 1);
    },
    { active: Boolean(token), everyMs: MENU_REFRESH_MS },
  );

  useEffect(() => {
    let cancelled = false;
    // No access token yet (e.g. right after a hard reload, before the
    // session is silently restored). Fetching /me/menu without a Bearer
    // token would 401 and surface a false "server unavailable" banner in
    // the Sidebar. Stay in the loading state instead and wait for the
    // token — the effect re-runs when `token` becomes available.
    if (!token) {
      setState({ menu: null, loading: true, error: null, unauthorized: false });
      return () => {
        cancelled = true;
      };
    }
    loadMenu(apiBase, token, channel)
      .then((tree) => {
        if (!cancelled) setState({ menu: tree, loading: false, error: null, unauthorized: false });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const unauthorized = e instanceof MenuFetchError && e.unauthorized;
        setState({
          menu: { items: [], sections: [] },
          loading: false,
          error: e instanceof Error ? e : new Error('Menu fetch failed'),
          unauthorized,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, token, channel, round]);

  return state;
}
